import { CommonModule, NgStyle } from '@angular/common';
import { Component, Injector, OnInit, computed, forwardRef, inject, input, model, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule } from '@angular/forms';
import { DxHtmlEditorModule, DxPopupModule, DxTabPanelComponent, DxTabPanelModule, DxTextAreaModule } from 'devextreme-angular';
import { ControlLabelComponent } from '../../internal/control-label/control-label.component';
import { ControlSupportingTextComponent } from '../../internal/control-supporting-text/control-supporting-text.component';
import { LayoutControlComponent } from '../../internal/layout-control/layout-control.component';
import { DxButtonTypes } from 'devextreme-angular/ui/button';
import { Clipboard } from '@angular/cdk/clipboard';
import { MacroDefinition } from 'src/app/mail-templates/models/macro-definition';
import { ValueChangedEvent as HtmlValueChangedEvent } from 'devextreme/ui/html_editor';
import { ValueChangedEvent as TextAreaValueChangedEvent } from 'devextreme/ui/text_area';

@Component({
  selector: 'app-html-editor',
  imports: [CommonModule, NgStyle, ControlLabelComponent, ControlSupportingTextComponent, LayoutControlComponent, DxTabPanelModule, DxHtmlEditorModule, DxTextAreaModule, ReactiveFormsModule, DxPopupModule],
  templateUrl: './html-editor.component.html',
  styleUrl: './html-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true
    }
  ],
})
export class HtmlEditorComponent implements OnInit, ControlValueAccessor {
  private dxTabPanel = viewChild.required<DxTabPanelComponent>('tabPanel');
  // private dxHtmlEditor = viewChild.required<DxHtmlEditorComponent>('htmlEditor');

  private injector = inject(Injector);
  private clipboard = inject(Clipboard);
  private ngControl = signal<NgControl>(null);
  public control = () => this.ngControl()?.control;

  public value = model<string | null>(null);
  public caption = input.required<string>();
  public height = input<string>('425px');
  public helpText = input<string>();
  public readonly = input<boolean>(false);
  public extendedToolbar = input<boolean>(false);
  public macros = input<MacroDefinition[]>([]);

  public showMacroButton = computed(() => (this.macros()?.length ?? 0) > 0);
  public showMacroPopup = false;
  showMacroButtonOptions: DxButtonTypes.Properties = {
    text: 'Show macros',
    icon: 'variable',
    stylingMode: 'text',
    onClick: () => this.showMacroPopup = true,
  };

  ngOnInit(): void {
    this.ngControl.set(this.injector.get(NgControl, null));
  }

  get isValid(): boolean {
    return !this.control() || (this.control().invalid !== true || (this.control().pristine && this.control().untouched));
  }

  copyToClipboard(macro: MacroDefinition): void {
    this.clipboard.copy(macro.Macro);
    this.showMacroPopup = false;
  }

  onInput(e: HtmlValueChangedEvent | TextAreaValueChangedEvent): void {
    if (e.value !== this.value()) {
      this.onChange(e.value as string);
      this.onTouched();
      this.value.set(e.value as string);
    }
  }

  // Begin ControlValueAccessor //

  writeValue(value: string | null): void {
    this.value.set(value);
  }

  onChange: (value: string) => void = () => { };
  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    //this.dxHtmlEditor().disabled = isDisabled;
    this.dxTabPanel().disabled = isDisabled;
  }

  // End ControlValueAccessor //
}
